import { cloneDeep, get, isEmpty, isEqual } from 'lodash';
import { reverse } from 'named-urls';
import moment from 'moment';
import { customNavigate } from '../common/hooks/useCustomNavigate';
import { PhotoUploadState } from '../constants/PhotoUploadState';
import { apiClient } from '../api/ApiClient';
import { handleError, showAlert } from '../common/components/alert/AlertActions';
import {
  ADD_PHOTO_IN_SERVICE_HISTORY_SUCCESS,
  ADD_PHOTO_IN_SUMMARY_SUCCESS,
  ADD_VIDEO_IN_SUMMARY_SUCCESS,
  AUCTION_CLEAR_SUCCESS,
  AUTOFILL_SUCCESS,
  CLEAR_BASIC_DATA_SAVED,
  CLEAR_DAMAGES_TO_DELETE,
  CLEAR_DAMAGES_TO_WITH_NO_DAMAGE_TYPE,
  CLEAR_PHOTOS_IN_SERVICE_HISTORY,
  CONFIRM_LAUNCH_FAILURE,
  DELETE_DAMAGE,
  DELETE_DAMAGE_SUCCESS,
  DELETE_PHOTO_SUCCESS,
  DELETE_VIDEO_IN_SUMMARY_SUCCESS,
  DISABLE_AUCTION_EDITING_SUCCESS,
  FETCH_ALL_VEHICLE_FEATURES_SUCCESS,
  FETCH_DESCRIPTION_TEMPLATE_SUCCESS,
  FETCH_ORGANIZATION_GROUP_SUCCESS,
  FETCH_ORGANIZATION_LOCATIONS_SUCCESS,
  HANDLE_UPLOAD_PHOTO_ERROR,
  PUBLISH_AUCTION_SUCCESS,
  READ_CAP_DATA_FAILURE,
  READ_CAP_DATA_SUCCESS,
  REDIRECT_TO_EDIT_FORM_STEP,
  REMOVE_PHOTO_IN_SERVICE_HISTORY,
  SAVE_AUCTION_SUCCESS,
  SET_ADD_VEHICLE_FORM_STEP,
  SET_ADDITIONAL_PHOTO_POSITION,
  SET_BACKEND_VALIDATION_ERRORS,
  SET_BASIC_DATA_SUCCESS,
  SET_DAMAGE_TYPE,
  SET_DRAFT_DATA_SUCCESS,
  SET_PHOTO_STEP_SUCCESS,
  SET_PHOTOS_SUCCESS,
  SORTED_PHOTOS_SUCCESS,
  UPDATE_DAMAGE,
  UPDATE_PHOTO_SUCCESS,
  UPDATE_PHOTOS_SUCCESS,
  UPDATE_SECTION_VALIDATION_ERRORS,
  UPDATE_VIDEO_SUCCESS,
  VALIDATE_BASIC_DATA_SUCCESS,
  VALIDATE_DESCRIPTION_AND_SETTINGS_SUCCESS,
  VALIDATE_SERVICE_HISTORY_SUCCESS,
  VALIDATE_TYRE_THREAD_DEPTHS_SUCCESS,
  VALIDATE_VEHICLE_FEATURES_SUCCESS,
  VALIDATE_VEHICLE_MEDIA_SUCCESS,
  VEHICLE_FOUND_FAILURE,
  VEHICLE_FOUND_SUCCESS,
  SET_DESCRIPTION_SUGGESTION,
  SET_GENERATING_DESCRIPTION,
  SET_DESCRIPTION_SUGGESTION_ATTEMPTS,
} from './AddVehicleReducer';
import { hideLoader, showLoader } from '../common/components/NavbarLoader/NavbarLoaderActions';
import AlertVariant from '../common/components/alert/AlertVariant';
import RouterPaths from '../constants/RouterPaths';
import { getPhotoDescription } from '../common/helpers/getPhotoDescription';
import { sortedByPerspective } from './helpers/sortByPerspective';
import { AuctionStatus } from '../constants/AuctionStatus';
import { AuctionStatusTypeName } from '../constants/AuctionStatusType';

import {
  ServiceHistory,
  ServiceHistoryName,
  ServiceTypeName,
} from '../constants/ServiceHistory';
import { TyreDepths } from '../constants/TyreDepths';

import {
  transformBasicDataToAPI,
  transformDescriptionAndSettingsToAPI,
  transformServiceHistoryToAPI,
  transformTyreThreadDepthsToAPI,
  transformVehicleDamagesToAPI,
  transformVehicleFeaturesToAPI,
  transformVehicleMediaToAPI,
  transformYesNoToBoolean,
} from './AddVehicleDataHelpers';
import { parseCurrencyToIntIfString } from '../common/helpers/parseCurrencyToIntIfString';
import { REVERSE_DASH_DATETIME_FORMAT } from '../common/utils/date/Date.utils';
import { capitalizeString } from '../common/helpers/capitalizeString';

const MEDIA_API_ENDPOINT = '/media';
const REGISTRATION_PALETE_ENDPOINT = '/cap/make';
const VALIDATE_BASIC_CAR_DATA_ENDPOINT = '/auction/validate-car-basic-data';
const VEHICLE_MAKE_LIST_ENDPOINT = '/vehicle-make';
const AUCTION_DRAFT_ENDPOINT = 'auction/draft';
const VALIDATE_VEHICLE_FEATURES_ENDPOINT = '/auction/validate-vehicle-specification';
const VALIDATE_VEHICLE_MEDIA_ENDPOINT = '/auction/validate-vehicle-media';
const VEHICLE_DESCRIPTION_TEMPLATE_ENDPOINT = '/vehicle-description-template';
const VEHICLE_DESCRIPTION_AND_SETTINGS_ENDPOINT = '/auction/validate-description-and-settings';
const VEHICLE_ORGANIZATION_DESCRIPTION_ENDPOINT = '/organization-member/default-description';
const ORGANIZATION_LOCATIONS_ENDPOINT = '/account/organization-locations';
const ALL_VEHICLE_FEATURES = '/vehicle-specification';
const ORGANIZATION_GROUP_ENDPOINT = '/account/organization-group';
const VALIDATE_TYRE_TREAD_DEPTHS_ENDPOINT = '/auction/validate-tyre-tread-depths';
const VALIDATE_SERVICE_HISTORY_ENDPOINT = '/auction/validate-service-history';
const AUCTION_ENDPOINT = '/auction';

// -- VALIDATION ACTIONS --

export const mapBasicDataErrors = (errors) => {
  const mappedErrors = {};
  if (errors) {
    Object.keys(errors).forEach((key) => {
      mappedErrors[key] = errors[key];
    });
  }
  return { VEHICLE_DETAILS: mappedErrors };
};

export const mapDescriptionErrors = (errors) => {
  const mappedErrors = {};
  if (errors) {
    Object.keys(errors).forEach((key) => {
      mappedErrors[key] = errors[key];
    });
  }
  return { DESCRIPTION: mappedErrors };
};

export const mapTyreTreadDepthsErrors = (errors) => {
  const mappedErrors = {};
  if (errors) {
    Object.keys(errors).forEach((key) => {
      mappedErrors[key] = errors[key];
    });
  }
  return { TYRES: mappedErrors };
};

export const mapVehicleMediaErrors = (errors) => {
  const mappedErrors = {};
  if (errors) {
    Object.keys(errors).forEach((key) => {
      mappedErrors[key] = errors[key];
    });
  }
  return { VEHICLE_MEDIA: mappedErrors };
};

export const mapVehicleSpecificationErrors = (errors) => {
  const mappedErrors = {};
  if (errors) {
    Object.keys(errors).forEach((key) => {
      mappedErrors[key] = errors[key];
    });
  }
  return { SPECIFICATION: mappedErrors };
};

export const mapServiceHistoryErrors = (errors) => {
  const mappedErrors = {};
  if (errors) {
    Object.keys(errors).forEach((key) => {
      mappedErrors[key] = errors[key];
    });
  }
  return { SERVICE_HISTORY: mappedErrors };
};

export const backendToFrontendFieldMappings = {
  // VEHICLE_DETAILS (as per your code)
  makeId: 'makeSelect',
  modelId: 'modelSelect',
  derivative: 'derivative',
  mileage: 'mileage',
  year: 'year',
  dateOfFirstRegistration: 'dateOfFirstRegistration',
  colour: 'colour',
  carType: 'carType',
  engineSize: 'engineSize',
  fuel: 'fuel',
  doors: 'doors',
  vin: 'vin',
  transmission: 'transmission',
  previousKeepersInTotal: 'previousKeepersInTotal',
  v5cLogbook: 'v5cLogBook',
  ulezCompliant: 'ulezCompliant',
  keys: 'keys',

  // DESCRIPTION_AND_SETTINGS
  description: 'description',
  location: 'vehicleLocation',
  type: 'type',
  auctionEndDate: 'auctionEndDate',
  autoAcceptPriceInPence: 'autoAcceptPriceInPence',
  minimumPriceInPence: 'minimumPriceInPence',

  // TYRE_TREAD_DEPTHS
  tyreTreadDepths: 'tyreTreadDepths',

  // VEHICLE_MEDIA
  photos: 'photos',
  videos: 'videos',

  // VEHICLE_SPECIFICATION
  vehicleFeaturesDictionary: 'vehicleFeaturesDictionary',
  vehicleFeaturesCustom: 'vehicleFeaturesCustom',

  // SERVICE_HISTORY
  serviceHistory: 'serviceHistory',

  // VEHICLE_REGISTRATION
  registrationPlate: 'registrationPlate',
};

export const mapBackendErrors = (errors, sectionName) => {
  const mappedErrors = {};
  if (errors) {
    Object.keys(errors).forEach((backendKey) => {
      const frontendKey = backendToFrontendFieldMappings[backendKey] || backendKey;
      mappedErrors[frontendKey] = errors[backendKey];
    });
  }
  return { [sectionName]: mappedErrors };
};

export const validateVehicleFeaturesLocal = (values) => (dispatch) => {
  const { vehicleFeaturesDictionary, vehicleFeaturesCustom } = values.vehicleFeatures;
  const errors = {};

  if (isEmpty(vehicleFeaturesDictionary) && isEmpty(vehicleFeaturesCustom)) {
    errors.vehicleFeaturesForm = 'There must be a minimum of one Vehicle Feature';
  }

  dispatch({
    type: SET_BACKEND_VALIDATION_ERRORS,
    payload: {
      VEHICLE_SPECIFICATION: errors,
    },
  });
};

export const validateBasicCarData = (values, errorCallback) => (dispatch) => {
  const payload = transformBasicDataToAPI(values);
  dispatch(showLoader());
  return apiClient
    .post(VALIDATE_BASIC_CAR_DATA_ENDPOINT, payload)
    .then((res) => {
      dispatch({ type: VALIDATE_BASIC_DATA_SUCCESS, payload: res.data });
      dispatch(saveBasicCarDataDraft(values));
      return res;
    })
    .catch((err) => {
      if (get(err, 'response.status', null) === 412) {
        if (typeof errorCallback === 'function') errorCallback(err.response.data.data);
      } else {
        dispatch(handleError(err));
      }
      throw err;
    })
    .finally(() => {
      dispatch(hideLoader());
    });
};

export const validateVehicleFeatures = (values, errorCallback) => (dispatch) => {
  const payload = transformVehicleFeaturesToAPI(values);
  dispatch(showLoader());
  return apiClient
    .post(VALIDATE_VEHICLE_FEATURES_ENDPOINT, payload)
    .then((res) => {
      dispatch({ type: VALIDATE_VEHICLE_FEATURES_SUCCESS, payload: res.data });
      return res;
    })
    .catch((err) => {
      if (get(err, 'response.status', null) === 412) {
        if (typeof errorCallback === 'function') errorCallback(err.response.data.data);
      } else {
        dispatch(handleError(err));
      }
      throw err;
    })
    .finally(() => {
      dispatch(hideLoader());
    });
};

export const validateMedia = (values, errorCallback) => (dispatch) => {
  const payload = transformVehicleMediaToAPI(values);
  dispatch(showLoader());
  return apiClient
    .post(VALIDATE_VEHICLE_MEDIA_ENDPOINT, payload)
    .then((res) => {
      dispatch({ type: VALIDATE_VEHICLE_MEDIA_SUCCESS, payload: res.data });
      return res;
    })
    .catch((err) => {
      if (get(err, 'response.status', null) === 412) {
        if (typeof errorCallback === 'function') errorCallback(err.response.data.data);
      } else {
        dispatch(handleError(err));
      }
      throw err;
    })
    .finally(() => {
      dispatch(hideLoader());
    });
};

export const validateTyres = (values, errorCallback) => (dispatch) => {
  const payload = transformTyreThreadDepthsToAPI(values);
  if (!payload || Object.keys(payload).length === 0) return Promise.resolve();
  dispatch(showLoader());
  return apiClient
    .post(VALIDATE_TYRE_TREAD_DEPTHS_ENDPOINT, payload)
    .then((res) => {
      dispatch({ type: VALIDATE_TYRE_THREAD_DEPTHS_SUCCESS, payload: res.data });
      return res;
    })
    .catch((err) => {
      if (get(err, 'response.status', null) === 412) {
        if (typeof errorCallback === 'function') errorCallback(err.response.data.data);
      } else {
        dispatch(handleError(err));
      }
      throw err;
    })
    .finally(() => {
      dispatch(hideLoader());
    });
};

export const validateServiceHistory = (values, errorCallback) => (dispatch) => {
  const payload = transformServiceHistoryToAPI(values);
  dispatch(showLoader());
  return apiClient
    .post(VALIDATE_SERVICE_HISTORY_ENDPOINT, payload)
    .then((res) => {
      dispatch({ type: VALIDATE_SERVICE_HISTORY_SUCCESS, payload: res.data });
      return res;
    })
    .catch((err) => {
      if (get(err, 'response.status', null) === 412) {
        if (typeof errorCallback === 'function') errorCallback(err.response.data.data);
      } else {
        dispatch(handleError(err));
      }
      throw err;
    })
    .finally(() => {
      dispatch(hideLoader());
    });
};

export const validateDescriptionAndSettings =
  (values, errorCallback) => (dispatch, getState) => {
    const { organizationLocations } = getState().addVehicle.toJS();
    dispatch(showLoader());
    const payload = transformDescriptionAndSettingsToAPI(values, organizationLocations);
    return apiClient
      .post(VEHICLE_DESCRIPTION_AND_SETTINGS_ENDPOINT, payload)
      .then((res) => {
        dispatch({ type: VALIDATE_DESCRIPTION_AND_SETTINGS_SUCCESS, payload: res.data });
        return res;
      })
      .catch((err) => {
        if (get(err, 'response.status', null) === 412) {
          if (typeof errorCallback === 'function') errorCallback(err.response.data.data);
        } else {
          dispatch(handleError(err));
        }
        throw err;
      })
      .finally(() => {
        dispatch(hideLoader());
      });
  };

// -- SAVE/PUBLISH ACTIONS --

export const saveCurrentSectionDraft = (values) => (dispatch, getState) => {
  const currentState = getState().addVehicle.toJS();

  // Determine which section changed and save only that section
  const changedSections = detectChangedSections(currentState, values);

  changedSections.forEach((section) => {
    dispatch(saveSectionDraft(section, values[section]));
  });
};

export const detectChangedSections = (currentState, newValues) => {
  const sections = [
    'basicData',
    'vehicleFeatures',
    'descriptionAndSettings',
    'tyreThreadDepths',
    'serviceHistory',
  ];
  return sections.filter((section) => !isEqual(currentState[section], newValues[section]));
};

export const saveSectionDraft = (section, sectionData) => (dispatch, getState) => {
  const addVehicleData = getState().addVehicle.toJS();
  const { version } = addVehicleData;

  const data = {
    ...addVehicleData,
    id: addVehicleData?.id,
    version,
    [section]: sectionData,
  };

  return apiClient
    .post(AUCTION_DRAFT_ENDPOINT, data)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: `SAVE_${section.toUpperCase()}_DRAFT_SUCCESS`,
          payload: { [section]: sectionData },
        });
      }
    })
    .catch((err) => dispatch(handleError(err)));
};

export const saveBasicCarDataDraft = (values, successCallback) => (dispatch, getState) => {
  dispatch(showLoader());
  const addVehicleData = getState().addVehicle.toJS();
  const { version } = addVehicleData;

  const data = {
    ...addVehicleData,
    id: addVehicleData?.id,
    version,
    basicData: {
      ...transformBasicDataToAPI(values),
      registrationPlate: values?.registrationPlate?.toUpperCase() || '',
      mot: values?.mot || null,
    },
  };

  return apiClient
    .post(AUCTION_DRAFT_ENDPOINT, data)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: SET_BASIC_DATA_SUCCESS,
          payload: {
            basicData: data?.basicData,
            id: res?.data?.data?.id,
          },
        });
        dispatch(showAlert('Draft saved successfully', AlertVariant.SUCCESS));
        if (typeof successCallback === 'function') {
          successCallback(res?.data?.data?.id);
        }
      }
    })
    .catch((err) => dispatch(handleError(err)))
    .finally(() => dispatch(hideLoader()));
};

export const saveDraft =
  (formData, successCallback, showSuccessAlert = true) =>
  (dispatch, getState) => {
    dispatch(showLoader());
    const addVehicleData = getState().addVehicle.toJS();
    const { organizationLocations, version, capId } = addVehicleData;
    const {
      basicData,
      vehicleFeatures,
      descriptionAndSettings,
      vehicleMedia,
      vehicleDamages,
      tyreThreadDepths,
      serviceHistory,
    } = formData;
    let syncedServiceHistory = serviceHistory;
    // Only sync photoRecords from Redux if service history type is not NONE
    // and if the form doesn't already have photoRecords defined
    const shouldSyncPhotos =
      serviceHistory?.type?.value !== ServiceHistory.NONE &&
      serviceHistory?.type !== ServiceHistory.NONE;

    if (
      shouldSyncPhotos &&
      addVehicleData.photoRecords &&
      Array.isArray(addVehicleData.photoRecords) &&
      addVehicleData.photoRecords.length > 0
    ) {
      if (!syncedServiceHistory || !Array.isArray(syncedServiceHistory.photoRecords)) {
        syncedServiceHistory = {
          ...syncedServiceHistory,
          photoRecords: addVehicleData.photoRecords,
        };
      } else if (
        addVehicleData.photoRecords.length !== syncedServiceHistory.photoRecords.length
      ) {
        syncedServiceHistory = {
          ...syncedServiceHistory,
          photoRecords: addVehicleData.photoRecords,
        };
      }
    }
    const payload = {
      id: addVehicleData?.id,
      vehicleCapCapId:
        capId || (basicData.vehicleCapCapId ? parseInt(basicData.vehicleCapCapId, 10) : null),
      version,
      basicData: {
        ...transformBasicDataToAPI(basicData),
        vehicleCapCapId:
          capId ||
          (basicData.vehicleCapCapId ? parseInt(basicData.vehicleCapCapId, 10) : null),
      },
      vehicleFeatures: transformVehicleFeaturesToAPI(vehicleFeatures),
      descriptionAndSettings: transformDescriptionAndSettingsToAPI(
        descriptionAndSettings,
        organizationLocations,
      ),
      vehicleMedia: transformVehicleMediaToAPI(vehicleMedia),
      vehicleDamages: transformVehicleDamagesToAPI(vehicleDamages),
      tyreThreadDepths: transformTyreThreadDepthsToAPI(tyreThreadDepths),
      serviceHistory: transformServiceHistoryToAPI(syncedServiceHistory),
    };

    if (payload.vehicleDamages === null) {
      payload.vehicleDamages = {
        damages: [],
        damagesToDelete: [],
      };
    }
    return apiClient
      .post(AUCTION_DRAFT_ENDPOINT, payload)
      .then((res) => {
        if (res.status === 200) {
          if (showSuccessAlert) {
            dispatch(showAlert('Draft saved successfully', AlertVariant.SUCCESS));
          }
          const newDraftId = res?.data?.data?.id;
          if (newDraftId) {
            dispatch(getDraft(newDraftId));
          }
          if (typeof successCallback === 'function') {
            successCallback(newDraftId);
          }
        }
        return res;
      })
      .catch((err) => {
        dispatch(handleError(err));
        throw err;
      })
      .finally(() => {
        dispatch(hideLoader());
      });
  };

export const saveAuction = (formData, redirectCallback) => (dispatch, getState) => {
  dispatch(showLoader());
  const addVehicleData = getState().addVehicle.toJS();
  const { organizationLocations, version, capId } = addVehicleData;
  const {
    basicData,
    vehicleFeatures,
    descriptionAndSettings,
    vehicleMedia,
    vehicleDamages,
    tyreThreadDepths,
    serviceHistory,
    id: draftId,
  } = formData;
  let syncedServiceHistory = serviceHistory;
  // Only sync photoRecords from Redux if service history type is not NONE
  // and if the form doesn't already have photoRecords defined
  const shouldSyncPhotos =
    serviceHistory?.type?.value !== ServiceHistory.NONE &&
    serviceHistory?.type !== ServiceHistory.NONE;

  if (
    shouldSyncPhotos &&
    addVehicleData.photoRecords &&
    Array.isArray(addVehicleData.photoRecords) &&
    addVehicleData.photoRecords.length > 0
  ) {
    if (!syncedServiceHistory || !Array.isArray(syncedServiceHistory.photoRecords)) {
      syncedServiceHistory = {
        ...syncedServiceHistory,
        photoRecords: addVehicleData.photoRecords,
      };
    } else if (
      addVehicleData.photoRecords.length !== syncedServiceHistory.photoRecords.length
    ) {
      syncedServiceHistory = {
        ...syncedServiceHistory,
        photoRecords: addVehicleData.photoRecords,
      };
    }
  }

  const payload = {
    id: draftId || null,
    vehicleCapCapId:
      capId || (basicData.vehicleCapCapId ? parseInt(basicData.vehicleCapCapId, 10) : null),
    basicData: {
      ...transformBasicDataToAPI(basicData),
      vehicleCapCapId:
        capId || (basicData.vehicleCapCapId ? parseInt(basicData.vehicleCapCapId, 10) : null),
    },
    vehicleFeatures: transformVehicleFeaturesToAPI(vehicleFeatures),
    descriptionAndSettings: transformDescriptionAndSettingsToAPI(
      descriptionAndSettings,
      organizationLocations,
    ),
    vehicleMedia: transformVehicleMediaToAPI(vehicleMedia),
    vehicleDamages: vehicleDamages
      ? transformVehicleDamagesToAPI(vehicleDamages)
      : {
          damages: [],
          damagesToDelete: [],
        },
    tyreThreadDepths: transformTyreThreadDepthsToAPI(tyreThreadDepths),
    serviceHistory: transformServiceHistoryToAPI(syncedServiceHistory),
    version,
  };

  if (payload.vehicleDamages === null) {
    payload.vehicleDamages = {
      damages: [],
      damagesToDelete: [],
    };
  }
  return apiClient
    .post(AUCTION_ENDPOINT, payload)
    .then((res) => {
      if (res.status === 200) {
        const saved = res?.data?.data;
        dispatch({ type: SAVE_AUCTION_SUCCESS, payload: saved });
        const endsOn = saved?.endsOn || payload?.descriptionAndSettings?.endsOn;
        const type = saved?.type || payload?.descriptionAndSettings?.type || 'PHYSICAL';
        const startsOn = saved?.startsOn || payload?.descriptionAndSettings?.startsOn;
        const publishPayload = {
          id: saved?.id,
          version: saved?.version,
          type,
          minimumPriceInPence:
            saved?.minimumPriceInPence !== undefined && saved?.minimumPriceInPence !== null
              ? parseCurrencyToIntIfString(saved.minimumPriceInPence)
              : null,
          autoAcceptPriceInPence:
            saved?.autoAcceptPriceInPence !== undefined &&
            saved?.autoAcceptPriceInPence !== null
              ? parseCurrencyToIntIfString(saved.autoAcceptPriceInPence)
              : null,
          timeOption: 'AUCTION_TIME_60',
          publishTarget: saved?.publishTarget?.value || saved?.publishTarget || 'EVERYWHERE',
          endsOn: endsOn || null,
          startsOn: startsOn || null,
          isBuyNowFeatureEnable:
            saved?.isBuyNowFeatureEnable ||
            payload?.descriptionAndSettings?.isBuyNowFeatureEnable ||
            false,
          buyNowPriceInPence:
            saved?.buyNowPriceInPence !== undefined && saved?.buyNowPriceInPence !== null
              ? parseCurrencyToIntIfString(saved.buyNowPriceInPence)
              : null,
          locationId:
            saved?.location?.id || payload?.descriptionAndSettings?.locationId || null,
        };
        return apiClient
          .post(`${AUCTION_ENDPOINT}/${saved?.id}/publish`, publishPayload)
          .then((pubRes) => {
            if (pubRes.status === 200) {
              const published = pubRes?.data?.data;
              dispatch({ type: PUBLISH_AUCTION_SUCCESS, payload: published });
              const status = published?.status;
              const PUBLISH_STATUS_MESSAGE_MAP = {
                SCHEDULED: 'Auction scheduled successfully!',
              };
              const message =
                PUBLISH_STATUS_MESSAGE_MAP[status] || 'Auction published successfully!';
              dispatch(showAlert(message, AlertVariant.SUCCESS));
              if (typeof redirectCallback === 'function') {
                redirectCallback(saved?.id);
              }
            }
            return pubRes;
          })
          .catch((err) => {
            const status = get(err, 'response.status', null);
            if (status === 412 || status === 418) {
              const backendErrors = err?.response?.data?.data;
              dispatch({ type: CONFIRM_LAUNCH_FAILURE, payload: { All: backendErrors } });
              dispatch({
                type: SET_BACKEND_VALIDATION_ERRORS,
                payload: backendErrors,
              });
              dispatch(
                showAlert('Please fix the validation errors before publishing.', 'danger'),
              );
            } else {
              dispatch(handleError(err));
            }
            throw err;
          });
      }
      return res;
    })
    .catch((err) => {
      const status = get(err, 'response.status', null);

      if (status === 412 || status === 418) {
        let backendErrors = err?.response?.data?.data;
        if (status === 418) {
          backendErrors = err?.response?.data;
        }
        dispatch({ type: SET_BACKEND_VALIDATION_ERRORS, payload: backendErrors });
        dispatch(showAlert('Please fix the validation errors before publishing.', 'danger'));
      } else {
        dispatch(handleError(err));
      }
      throw err;
    })
    .finally(() => dispatch(hideLoader()));
};

export const saveAuctionDraft = (formData, redirectCallback) => (dispatch, getState) => {
  dispatch(showLoader());
  const addVehicleData = getState().addVehicle.toJS();
  const { organizationLocations } = addVehicleData;
  const {
    basicData,
    vehicleFeatures,
    descriptionAndSettings,
    vehicleMedia,
    vehicleDamages,
    tyreThreadDepths,
    serviceHistory,
  } = formData;

  const payload = {
    basicData: transformBasicDataToAPI(basicData),
    vehicleFeatures: transformVehicleFeaturesToAPI(vehicleFeatures),
    descriptionAndSettings: transformDescriptionAndSettingsToAPI(
      descriptionAndSettings,
      organizationLocations,
    ),
    vehicleMedia: transformVehicleMediaToAPI(vehicleMedia),
    vehicleDamages: vehicleDamages
      ? transformVehicleDamagesToAPI(vehicleDamages)
      : {
          damages: [],
          damagesToDelete: [],
        },
    tyreThreadDepths: transformTyreThreadDepthsToAPI(tyreThreadDepths),
    serviceHistory: transformServiceHistoryToAPI(serviceHistory),
  };

  if (vehicleDamages === null) {
    payload.vehicleDamages = {
      damages: [],
      damagesToDelete: [],
    };
  }

  return apiClient
    .post(AUCTION_ENDPOINT, payload)
    .then((res) => {
      if (res.status === 200) {
        const saved = res?.data?.data;
        dispatch({ type: SAVE_AUCTION_SUCCESS, payload: saved });
        dispatch(showAlert('Auction saved as draft successfully!', AlertVariant.SUCCESS));
        if (typeof redirectCallback === 'function') {
          redirectCallback(saved?.id);
        }
      }
      return res;
    })
    .catch((err) => {
      if (get(err, 'response.status', null) === 412) {
        dispatch(showAlert('Please fix the validation errors before saving.', 'danger'));
      } else {
        dispatch(handleError(err));
      }
      throw err;
    })
    .finally(() => dispatch(hideLoader()));
};

export const saveToSendVehiclePhoto = (mediaFile, currentState, index) => (dispatch) => {
  const newState = [...currentState];
  newState[index] = {
    file: mediaFile,
    fileUrl: mediaFile.fileUrl,
    mediaMetadataId: null,
    status: PhotoUploadState.PENDING,
    perspective: getPhotoDescription(index + 1).PERSPECTIVE,
    damages: newState[index]?.damages ? newState[index]?.damages : [],
    damagesToDelete: newState[index]?.damagesToDelete ? newState[index]?.damagesToDelete : [],
  };

  dispatch({
    type: SET_PHOTOS_SUCCESS,
    payload: {
      photos: newState,
    },
  });
};

export const sortPhotosAndFillEmpty = (sentPhotos) => (dispatch) =>
  dispatch({
    type: SORTED_PHOTOS_SUCCESS,
    payload: {
      photos: sortedByPerspective(sentPhotos),
    },
  });

export const updatePhotos = (sentPhotos) => (dispatch) =>
  dispatch({
    type: UPDATE_PHOTOS_SUCCESS,
    payload: {
      photos: sentPhotos,
    },
  });

export const sendVehiclePhoto =
  (mediaFile, index, successCallback, noChangeUrl = false) =>
  (dispatch, getState) => {
    const formData = new FormData();
    formData.append('file', mediaFile);
    dispatch(showLoader());

    return apiClient
      .post(MEDIA_API_ENDPOINT, formData, {
        timeout: 60000,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        accept: '/',
      })
      .then((res) => {
        if (res.status === 200) {
          const responseData = res?.data?.data;
          const currentStateOfPhotos = getState().addVehicle.toJS().vehicleMedia?.photos || [];
          const currentPhotoState = currentStateOfPhotos[index] || {};

          const uploadedPhoto = {
            status: PhotoUploadState.SENT,
            fileUrl: noChangeUrl ? currentPhotoState.fileUrl : responseData.fileUrl,
            mediaMetadataId: responseData.mediaMetadataId || responseData.id,
            perspective: getPhotoDescription(index + 1).PERSPECTIVE,
            thumbnailUrl: responseData.thumbnailUrl,
            thumbnailKey: responseData.thumbnailKey,
            fileKey: responseData.fileKey,
            damages: currentPhotoState.damages || [],
            fileName: responseData.fileName,
            damagesToDelete: currentPhotoState.damagesToDelete || [],
            id: responseData.id,
          };

          dispatch({
            type: UPDATE_PHOTO_SUCCESS,
            payload: { photo: uploadedPhoto, index },
          });

          if (typeof successCallback === 'function') {
            successCallback(uploadedPhoto);
          }
          return uploadedPhoto;
        }
        return null;
      })
      .catch((err) => {
        const currentStateOfPhotos = getState().addVehicle.toJS().vehicleMedia?.photos || [];
        const photoToUpdate = cloneDeep(currentStateOfPhotos[index]);
        if (photoToUpdate) {
          photoToUpdate.status = PhotoUploadState.ERROR;
          dispatch({
            type: UPDATE_PHOTO_SUCCESS,
            payload: { photo: photoToUpdate, index },
          });
        }
        dispatch(handleError(err));
        dispatch((dispatch, getState) => {
          const photos = getState().addVehicle.toJS().vehicleMedia?.photos || [];
          if (photos[index] && photos[index].status === PhotoUploadState.PENDING) {
            const failedPhoto = { ...photos[index], status: 'ERROR' };
            dispatch({
              type: 'UPDATE_PHOTO_SUCCESS',
              payload: { photo: failedPhoto, index },
            });
          }
        });
        throw err;
      })
      .finally(() => dispatch(hideLoader()));
  };

export const sendDamagePhoto = (mediaFile) => (dispatch) => {
  const formData = new FormData();
  formData.append('file', mediaFile);
  dispatch(showLoader());
  return apiClient
    .post(MEDIA_API_ENDPOINT, formData, {
      timeout: 60000,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      accept: '/',
    })
    .then((res) => {
      if (res.status === 200) {
        const photoData = res?.data?.data;
        if (photoData) {
          return {
            status: PhotoUploadState.SENT,
            fileUrl: photoData.fileUrl,
            mediaMetadataId: photoData.mediaMetadataId || photoData.id,
            thumbnailUrl: photoData.thumbnailUrl,
            thumbnailKey: photoData.thumbnailKey,
            fileKey: photoData.fileKey,
            fileName: photoData.fileName,
            id: photoData.id,
          };
        }
      }
      return null;
    })
    .catch((err) => {
      dispatch(handleError(err));
      throw err;
    })
    .finally(() => dispatch(hideLoader()));
};

export const setDamageType = (damageType, damageIndex, photoIndex) => (dispatch, getState) => {
  const updatedPhotos = cloneDeep(getState().addVehicle.toJS()?.photos);

  updatedPhotos[photoIndex].damages[damageIndex] = {
    ...updatedPhotos[photoIndex].damages[damageIndex],
    damageType,
  };

  dispatch({
    type: SET_DAMAGE_TYPE,
    payload: {
      photos: updatedPhotos,
    },
  });
};

export const sendVehicleDamage =
  (mediaFile, currentState, sentPhotos, index) => (dispatch) => {
    const formData = new FormData();
    formData.append('file', mediaFile);
    dispatch(showLoader());
    return apiClient
      .post(MEDIA_API_ENDPOINT, formData, {
        timeout: 60000,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        accept: '/',
      })
      .then((res) => {
        if (res.status === 200) {
          const photoData = res?.data?.data;
          if (photoData) {
            const newDamage = {
              status: PhotoUploadState.SENT,
              fileUrl: photoData.fileUrl,
              mediaMetadataId: photoData.mediaMetadataId || photoData.id,
              thumbnailUrl: photoData.thumbnailUrl,
              thumbnailKey: photoData.thumbnailKey,
              fileKey: photoData.fileKey,
              fileName: photoData.fileName,
              id: photoData.id,
            };
            sentPhotos[index].damages = [...currentState, newDamage];
            dispatch({
              type: SET_PHOTOS_SUCCESS,
              payload: {
                photos: sentPhotos,
              },
            });
          }
        }
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(hideLoader()));
  };

export const sendPaleteNumber = (file, successCallback) => (dispatch) => {
  const message = { registrationPlate: file };
  dispatch(showLoader());
  return apiClient
    .post(REGISTRATION_PALETE_ENDPOINT, message)
    .then((res) => {
      if (res.status === 200) {
        const basicData = res?.data?.data;
        dispatch({
          type: READ_CAP_DATA_SUCCESS,
          payload: {
            basicData: res?.data?.data,
            registrationPlate: file,
            capId: res?.data?.data?.capId,
          },
        });
        dispatch({
          type: VEHICLE_FOUND_SUCCESS,
          payload: true,
        });
        dispatch({
          type: AUTOFILL_SUCCESS,
          payload: {
            autofill: null,
          },
        });
        // Fetch all models for the autofilled make if present
        if (basicData?.makeId) {
          dispatch(getVehicleModelList(basicData.makeId, () => {}));
        }
      }
      if (typeof successCallback === 'function') {
        successCallback();
      }
    })
    .catch((err) => {
      if (get(err, 'response.status', null) === 412) {
        if (typeof successCallback === 'function') {
          successCallback();
        }
        dispatch({
          type: READ_CAP_DATA_FAILURE,
          payload: {
            registrationPlate: file,
          },
        });
        dispatch({
          type: VEHICLE_FOUND_FAILURE,
          payload: false,
        });
      } else {
        dispatch(handleError(err));
      }
    })
    .finally(() => dispatch(hideLoader()));
};

export const getVehicleMakeList = (successCallback) => (dispatch) =>
  apiClient
    .get(VEHICLE_MAKE_LIST_ENDPOINT)
    .then((res) => successCallback(res?.data))
    .catch((err) => dispatch(handleError(err)));

export const getVehicleModelList = (vehicleMakeId, succesCallback) => (dispatch) =>
  apiClient
    .get(`${VEHICLE_MAKE_LIST_ENDPOINT}/${vehicleMakeId}`)
    .then((res) => succesCallback(res?.data))
    .catch((err) => dispatch(handleError(err)));

export const handleCarDataAutofil = (isAccepted) => (dispatch) => {
  dispatch({
    type: AUTOFILL_SUCCESS,
    payload: {
      autofill: isAccepted,
    },
  });
};

export const setPhotoStep = (currentStep) => (dispatch) => {
  dispatch({
    type: SET_PHOTO_STEP_SUCCESS,
    payload: {
      step: currentStep,
    },
  });
};

export const removePhotosSoft =
  (index, photos, vehicleMedia, photosToDelete) => (dispatch) => {
    const clonedData = cloneDeep(photos);
    clonedData[index].fileUrl = null;
    clonedData[index].mediaMetadataId = null;
    dispatch({
      type: DELETE_PHOTO_SUCCESS,
      payload: {
        photos: clonedData,
        vehicleMedia,
        photosToDelete: [
          ...photosToDelete,
          photos[index]?.data?.id || photos[index]?.mediaMetadataId,
        ],
      },
    });
  };

export const removePhotos = (index, photos, vehicleMedia, photosToDelete) => (dispatch) => {
  const clonedData = cloneDeep(photos);
  clonedData.splice(index, 1, { sortNumber: index });
  clonedData.forEach((item, index) => {
    item.perspective = getPhotoDescription(index + 1).PERSPECTIVE;
    item.sortNumber = index;
  });
  dispatch({
    type: DELETE_PHOTO_SUCCESS,
    payload: {
      photos: clonedData,
      vehicleMedia,
      photosToDelete: [
        ...photosToDelete,
        photos[index]?.data?.id || photos[index]?.mediaMetadataId,
      ],
    },
  });
};

export const removeDamages =
  (index, sentPhotos, damageId, damageIndex, vehicleMedia) => (dispatch) => {
    const clonedState = cloneDeep(sentPhotos);
    const clonedDamages = clonedState[index]?.damagesToDelete || [];
    clonedState[index].damagesToDelete = [...clonedDamages, damageId];
    clonedState[index].damages.splice(damageIndex, 1);

    dispatch({
      type: DELETE_DAMAGE_SUCCESS,
      payload: {
        vehicleMedia,
        photos: clonedState,
      },
    });
  };

export const clearDamagesToDelete = () => (dispatch, getState) => {
  const photos = getState()?.addVehicle?.toJS()?.photos;
  if (!photos) return;

  const clonedState = cloneDeep(photos);

  dispatch({
    type: CLEAR_DAMAGES_TO_DELETE,
    payload: {
      photos: clonedState.map((photo) => ({
        ...photo,
        damagesToDelete: [],
      })),
    },
  });
};

export const clearDamagesWithNoDamageType = () => (dispatch, getState) => {
  const photos = getState()?.addVehicle?.toJS()?.photos;
  if (!photos) return;

  const clonedState = cloneDeep(photos);

  dispatch({
    type: CLEAR_DAMAGES_TO_WITH_NO_DAMAGE_TYPE,
    payload: {
      photos: clonedState.map((photo) => ({
        ...photo,
        ...photo?.damages,
        damages: photo?.damages?.filter((el) => el.damageType),
      })),
    },
  });
};

export const fetchDescriptionTemplate = () => (dispatch) =>
  apiClient
    .get(VEHICLE_DESCRIPTION_TEMPLATE_ENDPOINT)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_DESCRIPTION_TEMPLATE_SUCCESS,
          payload: {
            descriptionTemplate: res?.data?.data,
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

export const fetchOrganizationLocations = () => (dispatch) =>
  apiClient
    .get(ORGANIZATION_LOCATIONS_ENDPOINT)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_ORGANIZATION_LOCATIONS_SUCCESS,
          payload: {
            organizationLocations: res?.data?.data,
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

export const fetchOrganizationGroup = () => (dispatch) =>
  apiClient
    .get(ORGANIZATION_GROUP_ENDPOINT)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_ORGANIZATION_GROUP_SUCCESS,
          payload: {
            organizationGroup: res?.data?.data,
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

export const fetchAllVehicleFeatures = () => (dispatch) => {
  dispatch(showLoader());
  return apiClient
    .get(ALL_VEHICLE_FEATURES)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_ALL_VEHICLE_FEATURES_SUCCESS,
          payload: {
            allVehicleFeatures: res?.data?.data,
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    })
    .finally(() => dispatch(hideLoader()));
};

export const getDraft = (auctionId, successCallback) => (dispatch, getState) => {
  if (auctionId) {
    dispatch(showLoader());
    return apiClient
      .get(`${AUCTION_DRAFT_ENDPOINT}/${auctionId}`)
      .then((res) => {
        if (res.status === 200) {
          const rawData = res?.data?.data;
          const transformKeysEnum = (numbersOfKeyIncluded) => {
            if (numbersOfKeyIncluded === 'ONE') return { value: '1', label: '1' };
            if (numbersOfKeyIncluded === 'TWO') return { value: '2', label: '2' };
            if (numbersOfKeyIncluded === 'TWO_PLUS') return { value: '3', label: '3' };
            return null;
          };

          const findLabelByValue = (constant, value) => {
            const entry = Object.entries(constant).find(([, val]) => val === value);
            return entry ? entry[0] : null;
          };

          const toSelectObjectFromNumber = (constant, numberValue) => {
            if (numberValue === null || numberValue === undefined) return null;
            const stringKey = findLabelByValue(constant, numberValue);
            return { value: stringKey, label: numberValue };
          };
          const transformedData = {
            ...rawData,
            basicData: rawData.basicData
              ? {
                  ...rawData.basicData,
                  makeSelect: rawData.basicData.makeId
                    ? {
                        value: rawData.basicData.makeId,
                        label: rawData.basicData.makeName || '',
                      }
                    : null,
                  modelSelect: rawData.basicData.modelId
                    ? {
                        value: rawData.basicData.modelId,
                        label: rawData.basicData.modelName || '',
                      }
                    : null,
                  mileage: rawData.basicData.mileage?.toString() || '',
                  year: rawData.basicData.year?.toString() || '',
                  doors: rawData.basicData.doors?.toString() || '',
                  engineSize: rawData.basicData.engineSize?.toString() || '',
                  previousKeepersInTotal:
                    rawData.basicData.previousKeepersInTotal?.toString() || '',
                  fuel: rawData.basicData.fuel
                    ? {
                        value: rawData.basicData.fuel,
                        label: rawData.basicData.fuel,
                      }
                    : null,
                  colour: rawData.basicData.colour
                    ? {
                        value: rawData.basicData.colour,
                        label: rawData.basicData.colour,
                      }
                    : null,
                  carType: rawData.basicData.carType
                    ? {
                        value: rawData.basicData.carType,
                        label: rawData.basicData.carType,
                      }
                    : null,
                  transmission: rawData.basicData.transmission
                    ? {
                        value: rawData.basicData.transmission,
                        label: rawData.basicData.transmission,
                      }
                    : null,
                  vehicleType: rawData.basicData.vehicleType
                    ? {
                        value: capitalizeString(rawData.basicData.vehicleType),
                        label: capitalizeString(rawData.basicData.vehicleType),
                      }
                    : null,
                  ulezCompliant: transformYesNoToBoolean(rawData.basicData.ulezCompliant),
                  vatQualifying:
                    rawData.basicData.vatQualifying != null
                      ? transformYesNoToBoolean(rawData.basicData.vatQualifying ? 'YES' : 'NO')
                      : null,
                  v5cLogBook: transformYesNoToBoolean(rawData.basicData.v5cLogbook),
                  keys: transformKeysEnum(rawData.basicData.numbersOfKeyIncluded),
                  dateOfFirstRegistration: (() => {
                    const input = rawData.basicData.dateOfFirstRegistration;
                    if (!input) return '';
                    const parsed = moment(input, 'YYYY-MM-DD', true);
                    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : input;
                  })(),
                }
              : null,
            descriptionAndSettings: (() => {
              if (!rawData.descriptionAndSettings) return null;

              const location = getState()
                .addVehicle.toJS()
                .organizationLocations?.find(
                  (loc) => loc.id === rawData.descriptionAndSettings.locationId,
                );

              return {
                ...rawData.descriptionAndSettings,
                type: rawData.descriptionAndSettings.type
                  ? {
                      value: rawData.descriptionAndSettings.type,
                      label: AuctionStatusTypeName[rawData.descriptionAndSettings.type],
                    }
                  : null,
                vehicleLocation: location
                  ? { value: location.id, label: location.postalCode?.postcode }
                  : null,
                minimumPriceInPence: rawData.descriptionAndSettings.minimumPriceInPence
                  ? rawData.descriptionAndSettings.minimumPriceInPence / 100
                  : null,
                autoAcceptPriceInPence: rawData.descriptionAndSettings.autoAcceptPriceInPence
                  ? rawData.descriptionAndSettings.autoAcceptPriceInPence / 100
                  : null,
                buyNowPriceInPence: rawData.descriptionAndSettings.buyNowPriceInPence
                  ? rawData.descriptionAndSettings.buyNowPriceInPence / 100
                  : null,
                auctionStartDate:
                  rawData.descriptionAndSettings.startsOn &&
                  rawData.descriptionAndSettings.startsOn !== 'null'
                    ? moment(
                        rawData.descriptionAndSettings.startsOn.replace(/\[.*\]$/, ''),
                      ).format(REVERSE_DASH_DATETIME_FORMAT)
                    : null,
                auctionEndDate:
                  rawData.descriptionAndSettings.endsOn &&
                  rawData.descriptionAndSettings.endsOn !== 'null'
                    ? moment(
                        rawData.descriptionAndSettings.endsOn.replace(/\[.*\]$/, ''),
                      ).format(REVERSE_DASH_DATETIME_FORMAT)
                    : null,
                termsAccepted: !!rawData.descriptionAndSettings.isBuyNowFeatureEnable,
                highlightedInfo: rawData.descriptionAndSettings.vehicleHighlightedInfo || '',
              };
            })(),
            vehicleFeatures: rawData.vehicleFeatures
              ? {
                  vehicleFeaturesDictionary:
                    rawData.vehicleFeatures.vehicleFeaturesDictionaryIds || [],
                  vehicleFeaturesCustom: rawData.vehicleFeatures.vehicleFeaturesCustom || [],
                }
              : {
                  vehicleFeaturesDictionary: [],
                  vehicleFeaturesCustom: [],
                },
            tyreThreadDepths: rawData.tyreThreadDepths
              ? {
                  nearsideFront: toSelectObjectFromNumber(
                    TyreDepths,
                    rawData.tyreThreadDepths.nearsideFront,
                  ),
                  nearsideRear: toSelectObjectFromNumber(
                    TyreDepths,
                    rawData.tyreThreadDepths.nearsideRear,
                  ),
                  offsideFront: toSelectObjectFromNumber(
                    TyreDepths,
                    rawData.tyreThreadDepths.offsideFront,
                  ),
                  offsideRear: toSelectObjectFromNumber(
                    TyreDepths,
                    rawData.tyreThreadDepths.offsideRear,
                  ),
                }
              : null,
            serviceHistory: rawData.serviceHistory
              ? {
                  ...rawData.serviceHistory,
                  type: rawData.serviceHistory.type
                    ? {
                        value: rawData.serviceHistory.type,
                        label: ServiceHistoryName[rawData.serviceHistory.type],
                      }
                    : null,
                  manualRecords: (rawData.serviceHistory.manualRecords || []).map(
                    (record) => ({
                      ...record,
                      serviceType: record.serviceType
                        ? {
                            value: record.serviceType,
                            label: ServiceTypeName[record.serviceType],
                          }
                        : null,
                    }),
                  ),
                }
              : null,
          };
          const draftData = transformedData;
          dispatch({
            type: SET_DRAFT_DATA_SUCCESS,
            payload: {
              ...draftData,
              vehicleMedia: {
                photos: draftData.vehicleMedia?.photos || [],
                videos: draftData.vehicleMedia?.videos || [],
              },
              serviceHistory: {
                ...draftData.serviceHistory,
                photoRecords: draftData.serviceHistory?.photoRecords || [],
              },
              damages: (draftData.vehicleDamages?.damages || []).map((damage) => ({
                ...damage,
                x: damage.coordinateX || damage.x || 0,
                y: damage.coordinateY || damage.y || 0,
                damageType: damage.type || damage.damageType,
                isPersisted: true,
                section: damage.section,
              })),
            },
          });

          if (typeof successCallback === 'function') {
            successCallback(draftData);
          }
        }
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => {
        dispatch(hideLoader());
      });
  }
  return null;
};

export const fetchOrganisationDescription = () => (dispatch) =>
  getOrganisationDescription()
    .then((res) => {
      dispatch({
        type: 'FETCH_ORGANISATION_DESCRIPTION_SUCCESS',
        payload: { defaultDescription: res?.data?.data || '' },
      });
    })
    .catch((err) => dispatch(handleError(err)));

export const saveDescriptionToOrganisation = (description = '') =>
  apiClient.post(VEHICLE_ORGANIZATION_DESCRIPTION_ENDPOINT, {
    defaultDescription: description.trim(),
  });

export const getOrganisationDescription = () =>
  apiClient.get(VEHICLE_ORGANIZATION_DESCRIPTION_ENDPOINT);

export const sendVehiclePhotoInSummary =
  (mediaFile, vehicleMedia, PhotoPerspective) => (dispatch) => {
    dispatch(showLoader());
    const formData = new FormData();
    formData.append('file', mediaFile);

    return apiClient
      .post(MEDIA_API_ENDPOINT, formData, {
        timeout: 60000,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        accept: '/',
      })
      .then((res) => {
        if (res.status === 200) {
          const photo = {
            fileUrl: res?.data?.data?.fileUrl,
            mediaMetadataId: res?.data?.data?.id,
            perspective: PhotoPerspective.OTHER,
          };

          dispatch({
            type: ADD_PHOTO_IN_SUMMARY_SUCCESS,
            payload: {
              photos: [...vehicleMedia.photos, photo],
              vehicleMedia,
            },
          });
        }
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(hideLoader()));
  };

export const uploadVehicleVideo = (file, thumbnail, index) => (dispatch) => {
  dispatch(showLoader());
  const videoFormData = new FormData();
  videoFormData.append('file', file);
  apiClient.defaults.timeout = 0;
  return apiClient
    .post(MEDIA_API_ENDPOINT, videoFormData)
    .then((videoRes) => {
      if (videoRes.status !== 200) throw new Error('Video upload failed');
      return apiClient.post(MEDIA_API_ENDPOINT, thumbnail.formData).then((thumbRes) => {
        if (thumbRes.status !== 200) throw new Error('Thumbnail upload failed');
        const finalVideo = {
          fileUrl: videoRes.data.data.fileUrl,
          mediaMetadataId: videoRes.data.data.id,
          previewUrl: thumbRes.data.data.fileUrl,
          previewMediaMetadataId: thumbRes.data.data.id,
        };
        dispatch({
          type: UPDATE_VIDEO_SUCCESS,
          payload: { video: finalVideo, index },
        });
      });
    })
    .catch((err) => {
      dispatch(handleError(err));
      dispatch((dispatch, getState) => {
        const videos = getState().addVehicle.toJS().vehicleMedia?.videos || [];
        if (videos[index] && videos[index].status === PhotoUploadState.PENDING) {
          const failedVideo = { ...videos[index], status: 'ERROR' };
          dispatch({
            type: 'UPDATE_VIDEO_SUCCESS',
            payload: { video: failedVideo, index },
          });
        }
      });
    })
    .finally(() => dispatch(hideLoader()));
};

export const sendVehicleVideoInSummary =
  (mediaFile, thumbnail, callback) => (dispatch, getState) => {
    dispatch(showLoader());
    const { vehicleMedia } = getState().addVehicle.toJS();

    const formData = new FormData();
    formData.append('file', mediaFile);
    apiClient.defaults.timeout = 0;
    return apiClient
      .post(MEDIA_API_ENDPOINT, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        accept: '/',
      })
      .then((res) => {
        if (res.status === 200) {
          dispatch(
            sendVehicleVideoThumbnail(thumbnail, (preview) => {
              const video = {
                fileUrl: res?.data?.data?.fileUrl,
                mediaMetadataId: res?.data?.data?.id,
                previewUrl: preview?.fileUrl,
                previewMediaMetadataId: preview?.id,
              };
              dispatch({
                type: ADD_VIDEO_IN_SUMMARY_SUCCESS,
                payload: {
                  videos: [...vehicleMedia.videos, video],
                  vehicleMedia,
                },
              });
              if (typeof callback === 'function') {
                callback({ video: res?.data?.data, thumbnail: preview });
              }
            }),
          );
        }
      })
      .catch((err) => {
        dispatch(handleError(err));
        if (typeof callback === 'function') {
          callback();
        }
      })
      .finally(() => dispatch(hideLoader()));
  };

export const prepareThumbnailFile = async (mediaFile) => {
  const res = await fetch(mediaFile);
  const blob = await res.blob();
  const file = new File([blob], 'thumbnail', { type: 'image/jpeg', lastModified: Date.now() });
  const formData = new FormData();
  formData.append('file', file);
  return formData;
};

const sendVehicleVideoThumbnail = (mediaFile, callback) => (dispatch) =>
  apiClient
    .post(MEDIA_API_ENDPOINT, mediaFile, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      accept: '/',
    })
    .then((res) => {
      if (res.status === 200) {
        callback(res?.data?.data);
      }
    })
    .catch((err) => dispatch(handleError(err)));

export const deleteVehicleVideoInSummary = (id) => (dispatch, getState) => {
  dispatch(showLoader());
  const { vehicleMedia } = getState().addVehicle.toJS();
  return apiClient
    .delete(`${MEDIA_API_ENDPOINT}/${id}`, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      accept: '/',
    })
    .then((res) => {
      if (res.status === 200) {
        dispatch(showAlert('Video removed successfully', AlertVariant.SUCCESS));
        const newVideos = vehicleMedia.videos.filter((item) => item.mediaMetadataId !== id);
        dispatch({
          type: DELETE_VIDEO_IN_SUMMARY_SUCCESS,
          payload: {
            videos: newVideos,
            vehicleMedia,
            videosToDelete: Array.isArray(vehicleMedia?.videosToDelete)
              ? [...vehicleMedia.videosToDelete, id]
              : [id],
          },
        });
      }
      return true;
    })
    .catch((err) => {
      dispatch(handleError(err));
    })
    .finally(() => dispatch(hideLoader()));
};

export const sendServiceHistoryPhotoRecords = (mediaFile) => (dispatch) => {
  dispatch(showLoader());
  const formData = new FormData();
  formData.append('file', mediaFile);

  return apiClient
    .post(MEDIA_API_ENDPOINT, formData, {
      timeout: 60000,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      accept: '/',
    })
    .then((res) => {
      if (res.status === 200) {
        const photoData = res?.data?.data;
        if (photoData) {
          const photoRecord = {
            status: PhotoUploadState.SENT,
            fileUrl: photoData.fileUrl,
            mediaMetadataId: photoData.id,
            thumbnailUrl: photoData.thumbnailUrl,
            thumbnailKey: photoData.thumbnailKey,
            fileKey: photoData.fileKey,
            fileName: photoData.fileName,
            id: photoData.id,
          };
          dispatch({
            type: ADD_PHOTO_IN_SERVICE_HISTORY_SUCCESS,
            payload: {
              photoRecord,
            },
          });
          return {
            type: ADD_PHOTO_IN_SERVICE_HISTORY_SUCCESS,
            payload: {
              photoRecord,
            },
          };
        }
      }
      return null;
    })
    .catch((err) => {
      dispatch(handleError(err));
      throw err;
    })
    .finally(() => dispatch(hideLoader()));
};

export const removePhotosFromServiceHistory = (id) => (dispatch, getState) => {
  const state = getState().addVehicle.toJS();
  const updatedPhotoRecords = state.photoRecords.filter((e) => e.mediaMetadataId !== id);

  dispatch({
    type: REMOVE_PHOTO_IN_SERVICE_HISTORY,
    payload: {
      photoRecords: updatedPhotoRecords,
    },
  });
};

export const handleUploadPhotoError = (value) => (dispatch) => {
  dispatch({ type: HANDLE_UPLOAD_PHOTO_ERROR, payload: value });
};

export const clearAuction = () => (dispatch) => {
  dispatch({ type: AUCTION_CLEAR_SUCCESS });
};

export const redirectToEdit =
  (auctionId, getDraftCallback, isDraft = false, navigate = null) =>
  (dispatch) => {
    dispatch({
      type: REDIRECT_TO_EDIT_FORM_STEP,
      payload: {
        formStep: 3,
        id: auctionId,
        isAuctionEditing: true,
        autofill: true,
      },
    });
    if (navigate) {
      navigate(`${RouterPaths.ADD_VEHICLE}/${auctionId}`, {
        state: {
          isDraft,
        },
      });
    }
    if (typeof getDraftCallback === 'function') {
      getDraftCallback();
    }
  };

export const setFormStep = (step) => (dispatch) => {
  dispatch({
    type: SET_ADD_VEHICLE_FORM_STEP,
    payload: {
      formStep: step,
    },
  });
};
export const disableAuctionEditing = () => (dispatch) => {
  dispatch({
    type: DISABLE_AUCTION_EDITING_SUCCESS,
  });
};

export const setAdditionalPhoto = (photoId, currentState, vehicleMedia) => (dispatch) => {
  const additionlPhoto = currentState.filter((element) => element.mediaMetadataId === photoId);
  const filteredPhotos = currentState.filter((element) => element.mediaMetadataId !== photoId);
  const restPhotos = filteredPhotos.slice(7, filteredPhotos.length);
  const mandatoryPhotos = filteredPhotos.slice(0, 7);
  const additionalPhotoNewPosition = [...mandatoryPhotos, ...additionlPhoto, ...restPhotos];

  dispatch({
    type: SET_ADDITIONAL_PHOTO_POSITION,
    payload: {
      vehicleMedia,
      photos: additionalPhotoNewPosition,
    },
  });
};

export const getAvailableDescriptionSuggestionAttempts = async (auctionId) => {
  const res = await apiClient.get(
    `${AUCTION_ENDPOINT}/${auctionId}/available-description-suggestion-attempts`,
  );
  return res?.data?.data ?? 0;
};

export const generateDescriptionSuggestion = async (auctionId, dispatch) => {
  try {
    const res = await apiClient.post(
      `${AUCTION_ENDPOINT}/${auctionId}/generate-description-suggestion`,
    );
    return res?.data?.data ?? null;
  } catch (err) {
    dispatch(showAlert(err?.response?.data?.message, AlertVariant.DANGER));
    return null;
  }
};

export const setDescriptionSuggestion = (content) => ({
  type: SET_DESCRIPTION_SUGGESTION,
  payload: content ?? null,
});

export const setGeneratingDescription = (isGenerating) => ({
  type: SET_GENERATING_DESCRIPTION,
  payload: !!isGenerating,
});

export const setDescriptionSuggestionAttempts = (attempts) => ({
  type: SET_DESCRIPTION_SUGGESTION_ATTEMPTS,
  payload: attempts ?? null,
});

export const editAuction =
  ({ auctionId, version, status }, navigate = null) =>
  (dispatch) => {
    if (
      status !== AuctionStatus.REJECTED &&
      status !== AuctionStatus.CANCELLED &&
      status !== AuctionStatus.ENDED
    ) {
      return dispatch(
        redirectToEdit(auctionId, () => dispatch(getDraft(auctionId)), true, navigate),
      );
    }
    dispatch(showLoader());
    return apiClient
      .post(`${AUCTION_ENDPOINT}/${auctionId}/edit`, { id: auctionId, version })
      .then((res) => {
        if (res.status === 200) {
          dispatch(
            redirectToEdit(
              res?.data?.data?.id,
              () => dispatch(getDraft(res?.data?.data?.id)),
              false,
              navigate,
            ),
          );
        }
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(hideLoader()));
  };

export const redirectToConfirmLaunchPage = (item, isRepublish) => () => {
  customNavigate(
    reverse(RouterPaths.MY_VEHICLES_DETAILS_CONFIRM, {
      id: item?.id,
    }),
    { allowRedirect: true, item, isRepublish },
  );
};

export const clearBasicDataSaved = () => (dispatch) => {
  dispatch({
    type: CLEAR_BASIC_DATA_SAVED,
  });
};

export const clearPhotosInServiceHistory = () => (dispatch) => {
  dispatch({
    type: CLEAR_PHOTOS_IN_SERVICE_HISTORY,
  });
};

export const updateDamage = (damageData) => (dispatch) => {
  dispatch({
    type: UPDATE_DAMAGE,
    payload: damageData,
  });
};

export const deleteDamage = (damageId) => (dispatch) => {
  dispatch({
    type: DELETE_DAMAGE,
    payload: { damageId },
  });
};

export const updateSectionValidation = (section, errors) => ({
  type: UPDATE_SECTION_VALIDATION_ERRORS,
  payload: { section, errors },
});
